import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useI18n } from "../i18n/I18nProvider";
import api from "../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function GallerySection() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const trackRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    api.get("/gallery").then(r => setItems(r.data || [])).catch(() => setItems([]));
  }, []);

  // Track which slide is centered while the user swipes/scrolls
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const onScroll = () => {
      const children = Array.from(el.children);
      if (!children.length) return;
      const center = el.scrollLeft + el.clientWidth / 2;
      let best = 0;
      let bestDist = Infinity;
      children.forEach((c, i) => {
        const mid = c.offsetLeft + c.clientWidth / 2;
        const d = Math.abs(mid - center);
        if (d < bestDist) { bestDist = d; best = i; }
      });
      setActiveIdx(best);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener("scroll", onScroll);
  }, [items]);

  const scrollTo = (idx) => {
    const el = trackRef.current;
    if (!el) return;
    const child = el.children[idx];
    if (child) el.scrollTo({ left: child.offsetLeft - (el.clientWidth - child.clientWidth) / 2, behavior: "smooth" });
  };
  const prev = () => scrollTo(Math.max(0, activeIdx - 1));
  const next = () => scrollTo(Math.min(items.length - 1, activeIdx + 1));

  if (!items.length) return null;

  return (
    <section id="gallery" className="relative py-24 sm:py-32 bg-[#050505]" data-testid="gallery-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-10">
        <div className="text-[11px] tracking-[0.35em] uppercase font-bold text-scooter-yellow mb-3">
          {t.gallery?.label || "Galleria"}
        </div>
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <h2 className="font-heading font-black text-white text-4xl sm:text-5xl lg:text-6xl tracking-tighter">
            {t.gallery?.title || "Momenti sui nostri scooter"}
          </h2>
          {items.length > 1 && (
            <div className="flex items-center gap-2" data-testid="gallery-controls">
              <button
                onClick={prev}
                disabled={activeIdx === 0}
                className="w-12 h-12 border border-white/20 text-white hover:border-scooter-yellow hover:text-scooter-yellow disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                aria-label="prev"
                data-testid="gallery-prev-btn"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={next}
                disabled={activeIdx === items.length - 1}
                className="w-12 h-12 border border-white/20 text-white hover:border-scooter-yellow hover:text-scooter-yellow disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                aria-label="next"
                data-testid="gallery-next-btn"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Swipe track */}
      <div
        ref={trackRef}
        className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hidden gap-6 px-4 sm:px-6 lg:px-8 pb-6"
        style={{ scrollbarWidth: "none" }}
        data-testid="gallery-track"
      >
        {items.map((it, i) => (
          <figure
            key={it.item_id}
            className="relative snap-center flex-shrink-0 w-[min(85vw,720px)] aspect-[4/5] sm:aspect-[16/10] group"
            data-testid={`gallery-item-${it.item_id}`}
            aria-label={it.alt || it.caption || ""}
          >
            <img
              src={`${BACKEND}/api/gallery/${it.item_id}/image`}
              alt={it.alt || it.caption || "Vico's Scooter"}
              loading={i === 0 ? "eager" : "lazy"}
              className="absolute inset-0 w-full h-full object-cover"
            />
            {/* Bottom gradient + caption overlay */}
            {it.caption && (
              <figcaption className="absolute inset-x-0 bottom-0 p-6 sm:p-8 text-white bg-gradient-to-t from-black/85 via-black/40 to-transparent">
                <p className="font-heading text-lg sm:text-2xl leading-snug tracking-tight drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)]">
                  {it.caption}
                </p>
                <div className="mt-2 h-[2px] w-10 bg-scooter-yellow" />
              </figcaption>
            )}
          </figure>
        ))}
      </div>

      {/* Dots */}
      {items.length > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4" data-testid="gallery-dots">
          {items.map((it, i) => (
            <button
              key={it.item_id}
              onClick={() => scrollTo(i)}
              className={`h-1.5 transition-all ${i === activeIdx ? "w-8 bg-scooter-yellow" : "w-4 bg-white/25 hover:bg-white/50"}`}
              aria-label={`slide ${i + 1}`}
              data-testid={`gallery-dot-${it.item_id}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}
